import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { FileText } from "lucide-react";

export default function PoliciesRates() {
  const policyImages = [
    {
      src: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/0c9ec27a-d452-4fe4-bfc3-b4f02ab07574/1.png",
      alt: "Tallahassee Lassie Pet Care policies - animal behavior, vaccinations, weather, and household emergencies",
    },
    {
      src: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/0a1a9dfc-3e67-499a-86aa-b6e72732e032/2.png",
      alt: "Tallahassee Lassie Pet Care policies - fences, emergency pet care, entry terms, security",
    },
    {
      src: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/37320950-8121-44ff-9483-71dbbb1ec478/3.png",
      alt: "Tallahassee Lassie Pet Care policies - liability, payment terms, holiday pricing",
    },
    {
      src: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/76e19633-7c05-4b07-8d6d-0539f8579f3b/4.png",
      alt: "Tallahassee Lassie Pet Care policies - delinquent payment, scheduling, cancellations",
    },
    {
      src: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/93cd8892-f2ab-4262-bc4b-b5aaf0540904/5.png",
      alt: "Tallahassee Lassie Pet Care policies - vacation pet-sitting, shared responsibility, privacy",
    },
  ];

  return (
    <div className="flex flex-col w-full">
      <SEO
        title="Policies & Rates | Pet Care Pricing"
        description="Tallahassee Lassie Pet Care policies and rates for dog walking, pet sitting, and all pet care services in Tallahassee, FL. Transparent pricing, cancellation policies, and service terms."
        path="/policies-rates"
      />
      <section className="relative bg-primary text-primary-foreground py-20 md:py-28 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center max-w-3xl">
          <Reveal>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Company Policies</h1>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-xl text-primary-foreground/85 leading-relaxed">
              Transparency is important to us. Here you'll find our complete company policies.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center shadow-md" style={{ borderRadius: "16px 0 16px 0" }}>
                <FileText size={24} />
              </div>
              <h2 className="font-serif text-3xl font-bold">Policies</h2>
            </div>
          </Reveal>

          <div className="space-y-6">
            {policyImages.map((img, i) => (
              <Reveal key={i}>
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full shadow-md border border-border/30"
                  style={{ borderRadius: "16px 0 16px 0" }}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center shadow-md" style={{ borderRadius: "16px 0 16px 0" }}>
                <FileText size={24} />
              </div>
              <h2 className="font-serif text-3xl font-bold">Rates Menu</h2>
            </div>
          </Reveal>
          <Reveal>
            <img
              src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/d88342e5-fd6e-4d94-9ded-268ea5e3d208/2022.2023+Tallahassee+Lassie+Menu+%28AO+updated%29+%281%29.png"
              alt="Tallahassee Lassie Pet Care services and rates menu with awards, contact info, and certifications"
              className="w-full shadow-md border border-border/30"
              style={{ borderRadius: "16px 0 16px 0" }}
            />
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <Reveal>
            <h2 className="font-serif text-3xl font-bold mb-4">Questions about our policies?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're happy to walk you through everything. Reach out and we'll get back to you quickly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg h-14 px-8">
                <Link href="/contact-us">
                  Contact Us
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg h-14 px-8 border-primary text-primary">
                <Link href="/menu">
                  View Services & Rates
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
